import React, { useCallback, useEffect, useState } from 'react';
import { Plus, ChevronDown, ChevronRight, Trash2, UserPlus, Shield } from 'lucide-react';
import api from '../../services/axios';
import { toast } from 'react-toastify';

const BLUE = '#0041C2';
const GRAD = 'linear-gradient(135deg,#0041C2,#1A5FD4)';

export default function SuperAdminView() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTenantId, setExpandedTenantId] = useState(null);
  const [tenantUsers, setTenantUsers] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantDesc, setNewTenantDesc] = useState('');
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserRole, setAddUserRole] = useState('USER');
  const [addingToTenant, setAddingToTenant] = useState(null);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get('/tenants');
      setTenants(res.data);
    } catch {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const fetchTenantUsers = async (tenantId) => {
    try {
      const res = await api.get(`/tenants/${tenantId}/users`);
      setTenantUsers(prev => ({ ...prev, [tenantId]: res.data }));
    } catch {
      toast.error('Failed to load users');
    }
  };

  const toggleExpand = (tenantId) => {
    if (expandedTenantId === tenantId) {
      setExpandedTenantId(null);
    } else {
      setExpandedTenantId(tenantId);
      fetchTenantUsers(tenantId);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (!newTenantName.trim()) return;
    try {
      await api.post('/tenants', { name: newTenantName.trim(), description: newTenantDesc.trim() });
      toast.success(`Tenant "${newTenantName}" created`);
      setNewTenantName('');
      setNewTenantDesc('');
      setShowCreateForm(false);
      fetchTenants();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create tenant');
    }
  };

  const handleDeactivateTenant = async (tenantId, name) => {
    if (!window.confirm(`Deactivate tenant "${name}"? Users will lose access.`)) return;
    try {
      await api.delete(`/tenants/${tenantId}`);
      toast.success(`Tenant deactivated`);
      fetchTenants();
    } catch {
      toast.error('Failed to deactivate tenant');
    }
  };

  const handleAddUser = async (e, tenantId) => {
    e.preventDefault();
    if (!addUserEmail.trim()) return;
    try {
      await api.post(`/tenants/${tenantId}/users`, { email: addUserEmail.trim(), role: addUserRole });
      toast.success(`User added to tenant`);
      setAddUserEmail('');
      setAddUserRole('USER');
      setAddingToTenant(null);
      fetchTenantUsers(tenantId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add user');
    }
  };

  const handleRemoveUser = async (tenantId, userId, email) => {
    if (!window.confirm(`Remove ${email} from this tenant?`)) return;
    try {
      await api.delete(`/tenants/${tenantId}/users/${userId}`);
      toast.success('User removed');
      fetchTenantUsers(tenantId);
    } catch {
      toast.error('Failed to remove user');
    }
  };

  const handleChangeRole = async (tenantId, userId, newRole) => {
    try {
      await api.put(`/tenants/${tenantId}/users/${userId}`, { role: newRole });
      toast.success('Role updated');
      fetchTenantUsers(tenantId);
    } catch {
      toast.error('Failed to update role');
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Platform Administration</h1>
          <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>Manage tenants and their users</p>
        </div>
        <button
          onClick={() => setShowCreateForm(s => !s)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: GRAD, color: '#fff', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', boxShadow: '0 0 14px rgba(0,65,194,0.3)',
          }}
        >
          <Plus size={15} /> New Tenant
        </button>
      </div>

      {/* Create tenant form */}
      {showCreateForm && (
        <form onSubmit={handleCreateTenant} style={{
          background: 'rgba(0,65,194,0.06)', border: '1px solid rgba(0,65,194,0.2)',
          borderRadius: 10, padding: 16, marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              value={newTenantName} onChange={e => setNewTenantName(e.target.value)}
              placeholder="Tenant name *" required
              style={{ flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 7, border: '1px solid #D1D5DB', fontSize: 13 }}
            />
            <input
              value={newTenantDesc} onChange={e => setNewTenantDesc(e.target.value)}
              placeholder="Description (optional)"
              style={{ flex: 2, minWidth: 200, padding: '8px 12px', borderRadius: 7, border: '1px solid #D1D5DB', fontSize: 13 }}
            />
            <button type="submit" style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: BLUE, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Create
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)} style={{ padding: '8px 12px', borderRadius: 7, border: '1px solid #D1D5DB', background: 'transparent', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Tenant list */}
      {tenants.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 14 }}>
          No tenants yet. Create one to get started.
        </div>
      )}
      {tenants.map(tenant => (
        <div key={tenant.id} style={{
          border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 10,
          overflow: 'hidden', background: '#fff',
          opacity: tenant.isActive ? 1 : 0.6,
        }}>
          {/* Tenant header row */}
          <div
            onClick={() => toggleExpand(tenant.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
              cursor: 'pointer', userSelect: 'none',
            }}
          >
            {expandedTenantId === tenant.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{tenant.name}</span>
                {!tenant.isActive && <span style={{ fontSize: 10, background: '#FEE2E2', color: '#DC2626', padding: '1px 6px', borderRadius: 4 }}>Inactive</span>}
              </div>
              <span style={{ fontSize: 11, color: '#6B7280' }}>{tenant.description || tenant.slug}</span>
            </div>
            <span style={{ fontSize: 12, color: '#6B7280' }}>{tenant.userCount ?? 0} users</span>
            {tenant.isActive && (
              <button
                onClick={e => { e.stopPropagation(); handleDeactivateTenant(tenant.id, tenant.name); }}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #FCA5A5', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 11 }}
              >
                Deactivate
              </button>
            )}
          </div>

          {/* Expanded user list */}
          {expandedTenantId === tenant.id && (
            <div style={{ borderTop: '1px solid #E5E7EB', padding: '12px 16px' }}>
              {/* Add user form */}
              {addingToTenant === tenant.id ? (
                <form onSubmit={e => handleAddUser(e, tenant.id)} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <input
                    value={addUserEmail} onChange={e => setAddUserEmail(e.target.value)}
                    placeholder="User email *" required type="email"
                    style={{ flex: 2, minWidth: 180, padding: '7px 10px', borderRadius: 7, border: '1px solid #D1D5DB', fontSize: 12 }}
                  />
                  <select
                    value={addUserRole} onChange={e => setAddUserRole(e.target.value)}
                    style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #D1D5DB', fontSize: 12 }}
                  >
                    <option value="COMPANY_USER">Company User</option>
                    <option value="COMPANY_MANAGER">Company Manager</option>
                    <option value="COMPANY_ADMIN">Company Admin</option>
                    <option value="COMPANY_OWNER">Company Owner</option>
                    <option value="USER">User</option>
                    <option value="TENANT_ADMIN">Tenant Admin</option>
                  </select>
                  <button type="submit" style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: BLUE, color: '#fff', fontSize: 12, cursor: 'pointer' }}>Add</button>
                  <button type="button" onClick={() => setAddingToTenant(null)} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #D1D5DB', background: 'transparent', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                </form>
              ) : (
                <button
                  onClick={() => setAddingToTenant(tenant.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: BLUE, background: 'transparent', border: `1px solid ${BLUE}`, padding: '5px 12px', borderRadius: 7, cursor: 'pointer', marginBottom: 10, fontWeight: 500 }}
                >
                  <UserPlus size={13} /> Add User
                </button>
              )}

              {/* Users table */}
              {(tenantUsers[tenant.id] || []).length === 0 ? (
                <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>No users in this tenant yet.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Role</th>
                      <th style={{ width: 60 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {(tenantUsers[tenant.id] || []).map(u => (
                      <tr key={u.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '8px 8px' }}>{u.firstName} {u.lastName}</td>
                        <td style={{ padding: '8px 8px', color: '#6B7280' }}>{u.email}</td>
                        <td style={{ padding: '8px 8px' }}>
                          <select
                            value={u.role}
                            onChange={e => handleChangeRole(tenant.id, u.userId, e.target.value)}
                            style={{ padding: '3px 7px', borderRadius: 5, border: '1px solid #D1D5DB', fontSize: 11 }}
                          >
                            <option value="COMPANY_USER">Company User</option>
                            <option value="COMPANY_MANAGER">Company Manager</option>
                            <option value="COMPANY_ADMIN">Company Admin</option>
                            <option value="COMPANY_OWNER">Company Owner</option>
                            <option value="USER">User</option>
                            <option value="TENANT_ADMIN">Tenant Admin</option>
                          </select>
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                          <button onClick={() => handleRemoveUser(tenant.id, u.userId, u.email)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
